/**
 * Sub-Admin Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { subAdminService } from '../services/subAdminService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  GetSubAdminsParams,
  CreateSubAdminPayload,
  UpdateSubAdminPayload,
} from '../types/subAdmin.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get sub-admins list hook
 */
export function useGetSubAdmins(
  params?: GetSubAdminsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.subAdmin,
      params?.page || 1,
      params?.limit || 10,
      params?.search || '',
      params?.permission,
    ],
    queryFn: () => subAdminService.getSubAdmins(params),
    ...options,
  });
}

/**
 * Get single sub-admin by ID hook
 */
export function useGetSubAdmin(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.subAdmin, id],
    queryFn: () => subAdminService.getSubAdmin(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create sub-admin hook
 */
export function useCreateSubAdmin(
  options?: UseMutationOptions<any, any, CreateSubAdminPayload, any>
) {
  return useApiMutation<any, any, CreateSubAdminPayload, any>({
    mutationFn: ((data: CreateSubAdminPayload) => subAdminService.createSubAdmin(data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.subAdmin] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Update sub-admin hook
 */
export function useUpdateSubAdmin(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateSubAdminPayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateSubAdminPayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateSubAdminPayload }) =>
      subAdminService.updateSubAdmin(variables.id, variables.data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.subAdmin] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Delete sub-admin hook
 */
export function useDeleteSubAdmin(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => subAdminService.deleteSubAdmin(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.subAdmin] });
    },
    ...options,
  });
}

